/**
 * 文档说明
 * @see https://www.yuque.com/docs/share/b961c389-fbc5-4428-9bc5-31ed924daea3?# （密码：wl36）
 */
import React from "react";
import OrderConsigneeDTO from "@/dto/order/OrderConsigneeDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import { Modal } from "antd";
import { AxiosRequestConfig } from "axios";
import { getSecTokenParams, getTopDataParams } from "./api/tb/orderSecurity";
import { getOrderConsignee, ListDecryptedConsigneeResult } from "./api/order";
import { v1VirtualMobileRegExp } from "@/utils/RegExp";
import ListDecryptedConsigneeError from "./utils/ListDecryptedConsigneeError";
import ShopType from "@/enum/ShopType";

declare global {
    interface GetSecTokenParams {
        /**
         * 御城河颁发的appKey
         */
        appKey: string;
        /**
         * 时间戳，格式为yyyy-MM-dd HH:mm:ss，时区为GMT+8，例如：2015-01-01 12:00:00。淘宝API服务端允许客户端请求最大时间误差为10分钟
         */
        time: string;
        /**
         * 登陆ISV系统的用户ID
         */
        userId: string;
        /**
         * 与淘宝关联的账号，如果没有直接关联店铺等
         */
        taobaoUserId: string;
        /**
         * 由开放平台top颁发的appkey，必须为线上的
         */
        topAppKey: string;
        /**
         * top注册的应用名字
         */
        appName: string;
        /**
         * 登陆用户的IP地址,具体获取方式见接入文档
         */
        userIp: string;
        /**
         * 唯一标识终端, 从浏览器的cookie获取
         */
        ati: string;
        /**
         * 步骤2.1生成的sign
         */
        sign: string;
        /**
         * 服务商的sessionId
         */
        sessionId: string;
    }

    interface GetTopDataParams {
        /**
         * 御城河颁发的appKey
         */
        appKey: string;
        /**
         * 时间戳，格式为yyyy-MM-dd HH:mm:ss，时区为GMT+8，例如：2015-01-01 12:00:00。淘宝API服务端允许客户端请求最大时间误差为10分钟
         */
        time: string;
        /**
         * 登陆ISV系统的用户ID
         */
        userId: string;
        /**
         * 与淘宝关联的账号，如果没有直接关联店铺等
         */
        taobaoUserId: string;
        /**
         * 由开放平台top颁发的appkey，必须为线上的
         */
        topAppKey: string;
        /**
         * top注册的应用名字
         */
        appName: string;
        /**
         * 登陆用户的IP地址,具体获取方式见接入文档
         */
        userIp: string;
        /**
         * 唯一标识终端，从浏览器cookie的里面获取
         */
        ati: string;
        /**
         * 步骤3.2生成的sign
         */
        sign: string;
        /**
         * 服务商sessionId
         */
        sessionId: string;
        /**
         * 登陆成功后获取的安全标识token
         */
        secToken: string;
        /**
         * top平台的token
         */
        topToken: string;
        /**
         * top API接口名称
         */
        method: string;
    }

    interface GetSecTokenByBXConfig extends AxiosRequestConfig {
        params: GetSecTokenParams;
    }

    interface SecTokenResponse {
        code: number;
        bizExtMap: any;
        headers: any;
        httpStatusCode: number;
        model: string;
        msgCode: any;
        msgInfo: any;
        success: string;
        msg: string;
        sub_msg: string;
    }

    interface GetTopDataByBXConfig extends AxiosRequestConfig {
        params: GetTopDataParams;
    }

    interface TopOaidClientDecryptResponse {
        code: number;
        msg: string;
        sub_code: string;
        sub_msg: string;
        request_id: string;
        receiver_list: {
            order_id: string;
            oaid: string;
            address_detail: string;
            town: string;
            district: string;
            city: string;
            state: string;
            country: string;
            phone: string;
            mobile: string;
            name: string;
            matched: boolean;
            secret_no_expire_time: string;
        }[];
    }

    var secToken: {
        /**
         * 获取sec_token步骤 有效期为1天
         */
        getSecTokenByBX(config: GetSecTokenByBXConfig): Promise<SecTokenResponse>;

        /**
         * 获取淘宝top接口数据
         */
        getTopDataByBX(config: GetTopDataByBXConfig): Promise<TopOaidClientDecryptResponse>;
    };
}

const secTokenMapping = new Map<
    number,
    {
        time: number;
        value: string;
    }
>();

async function validate(url: string) {
    // '{"type":"child","content":"{\\"smToken\\":\\"token\\",\\"queryToken\\":\\"sm\\",\\"sm\\":\\"sm\\"}"}'

    return new Promise<void>((yes, no) => {
        function onMessage(event) {
            try {
                const data = JSON.parse(event.data);

                if (data.type === "child") {
                    const content = JSON.parse(data.content);

                    if (content.smToken === "token") {
                        instance.destroy();

                        yes();
                    }
                }
            } catch (error) {}
        }

        window.addEventListener("message", onMessage);

        const instance = Modal.confirm({
            title: "请进行验证",
            width: 550,
            zIndex: 100000,
            content: (
                <div>
                    <iframe
                        src={url}
                        sandbox="allow-same-origin allow-top-navigation allow-forms allow-scripts"
                        style={{ width: "100%", height: 350, border: "none" }}
                    ></iframe>
                </div>
            ),
            okText: "知道了",
            okCancel: false,
            onOk() {
                window.removeEventListener("message", onMessage);
                no(new Error("用户取消"));
            },
            onCancel() {
                window.removeEventListener("message", onMessage);
                no(new Error("用户取消"));
            },
        });
    });
}

async function getSecToken(shopId: number) {
    const secToken = secTokenMapping.get(shopId);

    if (secToken?.value && Date.now() < secToken.time + 24 * 3600 * 1000) {
        return secToken.value;
    }

    // 获取secToken
    const secTokenParams = await getSecTokenParams(shopId);

    const secTokenResponse = await window.secToken.getSecTokenByBX({
        params: secTokenParams,
        headers: {
            "Content-Type": "Application/x-www-form-urlencoded",
        },
    });

    console.log(secTokenResponse);

    if (secTokenResponse?.["data"]?.["url"]) {
        await validate(secTokenResponse?.["data"]?.["url"]);

        return getSecToken(shopId);
    }

    if (secTokenResponse?.success === "false" || !secTokenResponse?.success) {
        const errorMessage = `解密发生错误：${secTokenResponse?.code || ""}${
            secTokenResponse?.sub_msg ||
            secTokenResponse?.msg ||
            secTokenResponse?.msgInfo ||
            JSON.stringify(secTokenResponse)
        }`;

        throw new ListDecryptedConsigneeError(errorMessage, ShopType.TAOBAO);
    }

    const realSecToken = secTokenResponse.model;

    setSecToken(shopId, realSecToken);

    return realSecToken;
}

function setSecToken(shopId: number, secToken: string) {
    secTokenMapping.set(shopId, {
        time: Date.now(),
        value: secToken,
    });
}

export async function tbClientOaidDecrypt(shopId: number, orderId: string, oaid: string, scene = "1008") {
    const secToken = await getSecToken(shopId);

    console.log(secToken);
    // https://open.taobao.com/doc.htm?spm=a219a.15212433.0.0.5da6669aOSyKSa&docId=120186&docType=1
    // 1008 查看详情地址
    // 1009 收件人姓名

    const topParams = await getTopDataParams(shopId, "taobao.top.oaid.client.decrypt", secToken);

    const response = await window.secToken.getTopDataByBX({
        params: topParams,
        headers: {
            "Content-Type": "Application/x-www-form-urlencoded",
        },
        data: `query_list={"tid":"${orderId}","oaid":"${oaid}","scene":"${scene}"}`,
    });

    if (response?.["data"]?.["url"]) {
        await validate(response?.["data"]?.["url"]);

        return tbClientOaidDecrypt(shopId, orderId, oaid);
    }

    if (!response?.receiver_list) {
        const errorMessage = `解密发生错误：${response?.code || ""}${
            response?.sub_msg || response?.["error_response"]?.["sub_msg"] || JSON.stringify(response)
        }`;

        throw new ListDecryptedConsigneeError(errorMessage, ShopType.TAOBAO);
    }

    const realConsignee = response?.receiver_list?.[0];

    if (!realConsignee) {
        throw new ListDecryptedConsigneeError("解密失败", ShopType.TAOBAO);
    }

    return realConsignee;
}

/**
 * 淘宝订单解密
 */
export default class TBOrderDecryptor {
    constructor(order: OrderDTO) {
        this.order = order;
    }

    order: OrderDTO;

    async decrypt(): Promise<ListDecryptedConsigneeResult> {
        const shopId = this.order.shopId;
        const isMergerOrder = this.order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT;
        const isSplitChildOrder = this.order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD;

        const orderConsignee = await getOrderConsignee(this.order.shopId, this.order.id);

        let orderOriginalId = this.order.originalId;
        const addressId = orderConsignee?.addressId;

        if (!addressId) {
            throw new ListDecryptedConsigneeError("订单收货人信息找不到addressId", ShopType.TAOBAO);
        }

        if (isMergerOrder && this.order?.childOrders?.length) {
            const childOrder = this.order.childOrders[0];

            orderOriginalId = childOrder.originalId;
        }

        if (isSplitChildOrder) {
            orderOriginalId = this.order.originalId.replace(/-[\d]{1,}$/, "");
        }

        return this._doDecrypt(shopId, orderOriginalId, addressId);
    }

    async _doDecrypt(shopId: number, orderOriginalId: string, oaid: string): Promise<ListDecryptedConsigneeResult> {
        let { address_detail, mobile, town, city, district, secret_no_expire_time } = await tbClientOaidDecrypt(
            shopId,
            orderOriginalId,
            oaid,
            "1008",
        );

        let { name } = await tbClientOaidDecrypt(shopId, orderOriginalId, oaid, "1009");

        if (!address_detail) {
            address_detail = this.order.consignee.street;
        }

        if (!mobile) {
            mobile = this.order.consignee.mobile;
        }

        if (!name) {
            name = this.order.consignee.name;
        }

        let privacyProtection = false;
        let virtualIdentifyNo;

        const virtualNoMatch = v1VirtualMobileRegExp.exec(mobile);

        if (virtualNoMatch) {
            privacyProtection = true;

            virtualIdentifyNo = virtualNoMatch[2];
        }

        const consigneeStreet = privacyProtection ? `${address_detail}[${virtualIdentifyNo}]` : address_detail;

        const consigneeName = privacyProtection ? `${name}[${virtualIdentifyNo}]` : name;

        const consigneeMobile = privacyProtection
            ? (mobile as string).replace(/-\d{4}$/, () => {
                  return "";
              })
            : mobile;

        const consignee: OrderConsigneeDTO = {
            ...this.order.consignee,
            street: consigneeStreet,
            mobile: consigneeMobile,
            name: consigneeName,
            city,
            town,
            district,
            privacyProtectionExpireTime: secret_no_expire_time,
            showTBMobileTip: true,
        };

        const listDecryptedConsigneeResult: ListDecryptedConsigneeResult = {
            id: this.order.id,
            consignee: {
                ...consignee,
                privacyProtection,
                virtualIdentifyNo,
            },
        };

        return listDecryptedConsigneeResult;
    }
}
